// src/screens/KioskCheckInScreen.js
// Kiosk check-in: the tablet camera scans the QR code the customer shows from
// the SelfDrive app, website or email. Fallback: the customer types their
// email and enters the 6-digit code we send them. The kiosk never asks for a
// password and never sees the customer's QR token itself.
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { AuthContext } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import CodeEntry from '../components/kiosk/CodeEntry';
import { appointmentsAPI, kioskAPI } from '../services/api';
import useKioskInactivity from '../hooks/useKioskInactivity';

const SCANNER_ID = 'kiosk-qr-reader';
const DONE_RESET_SECONDS = 8;

// QR codes may hold the bare token or a link ending in it.
const extractToken = (text) => {
  const match = /[a-f0-9]{64}/i.exec(text || '');
  return match ? match[0].toLowerCase() : null;
};

const KioskCheckInScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  useKioskInactivity(60000);

  const [mode, setMode] = useState('scan'); // scan | email | code | done
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [checkedIn, setCheckedIn] = useState(null);
  const [countdown, setCountdown] = useState(DONE_RESET_SECONDS);
  const scannerRef = useRef(null);
  const handlingRef = useRef(false);

  useEffect(() => {
    if (!user || user.role !== 'kiosk') navigate('/login', { replace: true });
  }, [user, navigate]);

  const finish = (appointment) => {
    setCheckedIn(appointment);
    setMode('done');
    setCountdown(DONE_RESET_SECONDS);
  };

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try { await scanner.stop(); } catch { /* already stopped */ }
      try { scanner.clear(); } catch { /* nothing to clear */ }
    }
  }, []);

  const onScan = useCallback(async (text) => {
    if (handlingRef.current) return;
    const token = extractToken(text);
    if (!token) {
      setError('That QR code is not a SelfDrive check-in code.');
      return;
    }
    handlingRef.current = true;
    setBusy(true);
    setError('');
    try {
      const { data } = await appointmentsAPI.checkIn(token);
      await stopScanner();
      finish(data.appointment);
    } catch (err) {
      setError(err.response?.data?.message || 'Check-in failed. Please see a staff member.');
    } finally {
      setBusy(false);
      // Pause briefly so one QR code held in front of the camera is not resubmitted repeatedly.
      setTimeout(() => { handlingRef.current = false; }, 2500);
    }
  }, [stopScanner]);

  // Start the camera while in scan mode.
  useEffect(() => {
    if (mode !== 'scan') return undefined;
    let cancelled = false;
    const scanner = new Html5Qrcode(SCANNER_ID);
    scannerRef.current = scanner;
    scanner
      // 'user' = the front camera, facing the customer on a mounted tablet
      .start({ facingMode: 'user' }, { fps: 10, qrbox: { width: 260, height: 260 } }, onScan, () => {})
      .catch(() => {
        if (!cancelled) {
          setError('The camera is not available. Use an email code instead.');
        }
      });
    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [mode, onScan, stopScanner]);

  // After a successful check-in, return to the kiosk home screen.
  useEffect(() => {
    if (mode !== 'done') return undefined;
    if (countdown === 0) {
      navigate('/kiosk', { replace: true });
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [mode, countdown, navigate]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await kioskAPI.requestCheckInCode(email.trim());
      setVerificationId(data.verificationId);
      setMode('code');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send a code. Please see a staff member.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (code) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await kioskAPI.verifyCheckInCode(verificationId, code);
      finish(data.appointment);
    } catch (err) {
      setError(err.response?.data?.message || 'That code did not work.');
    } finally {
      setBusy(false);
    }
  };

  const firstName = checkedIn?.user?.name?.split(' ')[0];
  const vehicle = checkedIn?.vehicles?.[0];

  return (
    <div className="flex flex-col h-full max-w-xl mx-auto w-full">
      <AppHeader title="Check In" />

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {mode === 'scan' && (
          <>
            <p className="text-center text-sm text-app-text-secondary-light dark:text-app-text-secondary">
              Hold up the QR code from your SelfDrive appointment.
            </p>
            <div id={SCANNER_ID} className="w-full rounded-2xl overflow-hidden bg-black min-h-[300px]" />
            {busy && <p className="text-center text-xs">Checking in…</p>}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}
            <button
              onClick={() => { setError(''); setMode('email'); }}
              className="w-full py-4 rounded-2xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-base font-semibold"
            >
              No QR code? Use an email code
            </button>
          </>
        )}

        {mode === 'email' && (
          <form onSubmit={requestCode} className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-6 border border-app-border-light dark:border-app-border space-y-4">
            <div className="text-center text-sm text-app-text-light dark:text-app-text">
              Enter the email you booked with. We&apos;ll send you a code.
            </div>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-3 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-base text-app-text-light dark:text-app-text outline-none focus:border-app-accent"
            />
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="w-full py-4 rounded-2xl bg-app-accent text-black text-base font-semibold disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
            <button type="button" onClick={() => { setError(''); setMode('scan'); }} className="w-full py-2 text-xs underline">
              Back to QR scan
            </button>
          </form>
        )}

        {mode === 'code' && (
          <CodeEntry email={email} onSubmit={verifyCode} onResend={() => requestCode()} submitting={busy} error={error} />
        )}

        {mode === 'done' && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-8 border border-app-border-light dark:border-app-border text-center space-y-3">
            <div className="text-4xl">✓</div>
            <div className="text-xl font-bold">Welcome{firstName ? `, ${firstName}` : ''}!</div>
            <div className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
              You&apos;re checked in{vehicle ? ` for the ${vehicle.year} ${vehicle.make} ${vehicle.model}` : ''}. A team member will be with you shortly.
            </div>
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              Returning to the start screen in {countdown}s
            </div>
          </div>
        )}

        <button
          onClick={() => navigate('/kiosk', { replace: true })}
          className="w-full py-3 rounded-2xl text-sm text-app-text-secondary-light dark:text-app-text-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default KioskCheckInScreen;
